import { uniq } from "@-/com.npmjs.array-utils-ts";
import { and, asc, like, or, sql } from "@-/com.npmjs.drizzle-orm";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import type { searchMorphemes } from "#search-morphemes-core";
import { loadHanziScriptConverters } from "@-/dev.improve.haniverse.lib.hanzi-script/converters";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";

export type SearchSentence = { id: number; sentence: string; translation: string | null };
const HAN_PATTERN = /\p{Script=Han}/u;
// String.match resets lastIndex before each use.
// oxlint-disable-next-line no-top/no-top-level-state
const ENGLISH_TOKEN_PATTERN = /[a-z]+/g;
const SCAN_BATCH_SIZE = 256;

function englishTokens(text: string): string[] {
  return text.toLowerCase().match(ENGLISH_TOKEN_PATTERN) ?? [];
}

/** A candidate scan has no result cap: pagination can reach every matching corpus sentence. */
export function getSentenceSearchPage(
  staticDb: Parameters<typeof searchMorphemes>[1],
  { offset, query, script = "simplified" }: { offset: number; query: string; script?: HanziScript },
) {
  return withDeadline(
    { deadline: 15_000, operation: "Search corpus sentences" },
    async (signal) => {
      // Load the English dictionaries only when searching the sentence corpus.
      const { adjective, englishInflections, noun, verb } =
        await import("@-/com.npmjs.wink-lemmatizer");
      const lemmas = (word: string) => uniq([word, noun(word), verb(word), adjective(word)]);
      const converters = await loadHanziScriptConverters();
      const trimmedQuery = query.trim();
      const isChinese = HAN_PATTERN.test(trimmedQuery);
      const queryTokens = englishTokens(trimmedQuery).map((word) => lemmas(word));
      if (!isChinese && queryTokens.length === 0) {
        return { hasMoreSentences: false, nextOffset: offset, sentences: [] };
      }
      const chineseForms = uniq([
        trimmedQuery,
        converters.traditionalToSimplified(trimmedQuery),
        converters.simplifiedToTraditional(trimmedQuery),
      ]);
      const sentence = staticSchema.sentence;
      // SQL narrows by surface forms; token checks below exclude interior substrings and handle
      // punctuation, case, irregular nouns/verbs and partial English words consistently.
      const predicate = isChinese
        ? or(...chineseForms.map((form) => sql`instr(${sentence.sentence}, ${form}) > 0`))
        : and(
            ...englishTokens(trimmedQuery).map((token) => {
              const forms = uniq(englishInflections(token));
              // `goose` already covers `gooses`; only genuinely different spellings (`geese`,
              // `goosing`) need another predicate. This keeps both the scan and its bindings small.
              const needles = forms.filter(
                (form) => !forms.some((other) => other !== form && form.includes(other)),
              );
              return or(...needles.map((form) => like(sentence.translation, `%${form}%`)));
            }),
          );
      const pageSize = offset === 0 ? 12 : 40;
      const matches: SearchSentence[] = [];
      let cursor = offset;
      while (!signal.aborted) {
        const rows = await staticDb(import.meta.location)
          .select({
            id: sentence.id,
            sentence: sentence.sentence,
            translation: sentence.translation,
          })
          .from(sentence)
          .where(predicate)
          .orderBy(asc(sql`length(${sentence.sentence})`), asc(sentence.id))
          .limit(SCAN_BATCH_SIZE)
          .offset(cursor);
        for (const row of rows) {
          const tokens = isChinese
            ? []
            : englishTokens(row.translation ?? "").map((word) => lemmas(word));
          const matchesQuery =
            isChinese ||
            queryTokens.every((candidates) =>
              tokens.some((token) =>
                token.some((lemma) => candidates.some((candidate) => lemma.startsWith(candidate))),
              ),
            );
          if (matchesQuery) {
            // Leave the lookahead hit unconsumed so the next page starts exactly here.
            if (matches.length === pageSize) {
              return { hasMoreSentences: true, nextOffset: cursor, sentences: matches };
            }
            matches.push({
              ...row,
              sentence:
                script === "traditional"
                  ? converters.simplifiedToTraditional(row.sentence)
                  : converters.traditionalToSimplified(row.sentence),
            });
          }
          cursor += 1;
        }
        if (rows.length < SCAN_BATCH_SIZE) {
          return { hasMoreSentences: false, nextOffset: cursor, sentences: matches };
        }
      }
      throw new Error("Sentence search aborted");
    },
  );
}
